// Users service - handles all user-related API calls
import { apiClient } from '@/lib/api-client';
import type { User, UserSearchResult, UserUpdatePayload } from '@/types/user';

/**
 * Search for users across all schools
 */
export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  if (query.length <= 3) return [];
  
  const sanitizedQuery = query.replace('+1', '');
  const params = new URLSearchParams();
  params.append('query', sanitizedQuery);

  const response = await apiClient.get<UserSearchResult[]>(
    `/v2/admin/users/search?${params.toString()}`
  );
  
  return response.data;
}

/**
 * Get all users for a specific school
 */
export async function getUsersBySchool(schoolId: string): Promise<User[]> {
  const response = await apiClient.get<User[]>(`/v2/schools/${schoolId}/users`);
  
  // Sort by name for consistent ordering
  return response.data.sort((a, b) => {
    const firstNameCompare = a.firstName.localeCompare(b.firstName);
    if (firstNameCompare !== 0) return firstNameCompare;
    return (a.lastName || '').localeCompare(b.lastName || '');
  });
}

/**
 * Get a specific user by ID
 */
export async function getUserById(userId: number): Promise<User> {
  const response = await apiClient.get<User>(`/v2/admin/user/${userId}`);
  return response.data;
}

/**
 * Update a user
 */
export async function updateUser(
  userId: number,
  data: UserUpdatePayload
): Promise<User> {
  const response = await apiClient.put<User>(`/v2/admin/user/${userId}`, data);
  return response.data;
}

/**
 * Delete a user
 */
export async function deleteUser(userId: number): Promise<void> {
  await apiClient.delete(`/v2/admin/user/${userId}`);
}

/**
 * Toggle ambassador status for a user
 */
export async function toggleAmbassador(
  userId: number,
  toggledOn: boolean
): Promise<void> {
  await apiClient.patch(`/v2/admin/user/${userId}/ambassador`, null, {
    params: { toggled_on: toggledOn },
  });
}

